import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Instagram, Youtube, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getPostAuthDestination } from "@/lib/postAuthRouting";
import { WattsLogo } from "@/components/WattsLogo";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  component: ComingSoon,
});

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function ComingSoon() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    getPostAuthDestination(user.id).then((to) => navigate({ to }));
  }, [user, loading, navigate]);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast.error("Email no válido");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("waitlist").insert({ email: value });
    setSubmitting(false);
    if (error && !/duplicate|unique/i.test(error.message)) {
      toast.error("No hemos podido guardarlo. Inténtalo de nuevo.");
      return;
    }
    setDone(true);
    setEmail("");
    toast.success("¡Te avisaremos cuando abramos! ⚡");
  };

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-6 py-12"
      style={{ background: "#0f0f0f", fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <div
        className="flex w-full max-w-md flex-col items-center text-center"
        style={{ animation: "wattsFadeIn 0.6s ease forwards", opacity: 0 }}
      >
        <WattsLogo width={180} />

        <h1
          className="mt-8 text-white"
          style={{
            fontFamily: "'Barlow Condensed', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 36,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
          }}
        >
          Algo grande se acerca.
        </h1>

        <p className="mt-3" style={{ color: "#888888", fontSize: 16 }}>
          La comunidad ciclista que estabas esperando.
        </p>

        <form onSubmit={onSubmit} className="mt-10 flex w-full gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Tu email"
            disabled={submitting || done}
            className="flex-1 rounded-full border border-[#222] bg-[#1a1a1a] px-5 py-3 text-sm text-white placeholder:text-[#666] focus:border-[#f5c518] focus:outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={submitting || done}
            className="rounded-full px-5 py-3 text-sm font-semibold transition-opacity disabled:opacity-60"
            style={{ background: "#f5c518", color: "#000" }}
          >
            {done ? "✓" : submitting ? "..." : "Avisadme"}
          </button>
        </form>

        <p
          className="mt-6"
          style={{ color: "#f5c518", fontSize: 13, letterSpacing: "0.02em" }}
        >
          Queda. Pedalea. Comparte.
        </p>

        <div className="mt-12 flex items-center gap-5">
          <a
            href="#"
            aria-label="X"
            className="text-[#666] transition-colors hover:text-[#f5c518]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2H21l-6.52 7.45L22 22h-6.84l-4.78-6.27L4.8 22H2l7-8L2 2h6.91l4.32 5.71L18.244 2zm-2.39 18h1.86L7.27 4H5.29l10.564 16z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/watts__app?igsh=MTl1aWJiZ3NxZHBpNA%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="text-[#666] transition-colors hover:text-[#f5c518]"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <a
            href="#"
            aria-label="YouTube"
            className="text-[#666] transition-colors hover:text-[#f5c518]"
          >
            <Youtube className="h-5 w-5" />
          </a>
          <a
            href="#"
            aria-label="LinkedIn"
            className="text-[#666] transition-colors hover:text-[#f5c518]"
          >
            <Linkedin className="h-5 w-5" />
          </a>
          <a
            href="#"
            aria-label="TikTok"
            className="text-[#666] transition-colors hover:text-[#f5c518]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
            </svg>
          </a>
        </div>
      </div>

      <p
        className="absolute bottom-4 text-center"
        style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 11, color: "#444444" }}
      >
        © Watts 2026 — Todos los derechos reservados
      </p>

      <style>{`@keyframes wattsFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
